import { router } from '@inertiajs/react'
import type { AppShellProps } from '@/components/AppShell'
import RegistrationPageLayout from '@/components/RegistrationPageLayout'
import { Button } from '@/components/ui/button'
import { CheckCircle, ArrowLeft, Clock, FileText, Download } from 'lucide-react'
import type { EventTheme } from '@/lib/registrationFormShared'
import { resolveAgendaDayLabel } from '@/lib/agendaDayLabel'
import { formatAgendaDayDate } from '@/lib/agendaDayDate'
import { SessionTimeRange } from '@/components/SessionClockTime'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'

interface AgendaPdf {
  filename: string
  url: string
}

interface RegistrationTicket {
  token: string
  firstName: string | null
  lastName: string | null
  userType: string | null
  qrCodeDataUrl: string
  badgeUrl: string
  downloadUrl: string
  agendaPdf?: AgendaPdf | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: EventTheme
  currentLocale: string
  locales: string[]
  approvalStatus?: 'pending' | 'approved' | 'rejected'
  eventUser: {
    token: string
    registrationKey?: string | null
    firstName: string | null
    lastName: string | null
    userType: string | null
  } | null
  multiStep: boolean
  qrCodeDataUrl: string | null
  registrationTickets: RegistrationTicket[]
  agendaPdf?: AgendaPdf | null
  message: string | null
  selectedSessions: Array<{
    id: number
    dayNumber: number | null
    dayLabel?: string
    dayDate?: string | null
    time: string
    endTime?: string
    description: string
  }>
  registrationLinkSlug?: string | null
  urls: AppShellProps['urls'] & { register: string }
  translations: AppShellProps['translations'] & {
    registrationSuccess: string
    registrationPending?: string
    registrationRejected?: string
    backToRegistration: string
    ticketsIssued: string
    openTicket: string
    downloadTicket: string
    entranceTicket?: string
    agendaPdf?: string
    viewAgenda?: string
    downloadAgenda?: string
    yourSelectedSessions: string
    sessionArrivalNotice: string
    yourRegistrationKey: string
    registrationKeyHint: string
    register: string
    day: string
  }
}

interface SelectedSession {
  id: number
  dayNumber: number | null
  dayLabel?: string
  dayDate?: string | null
  time: string
  endTime?: string
  description: string
}

function groupSelectedSessions(sessions: SelectedSession[]) {
  const groups: {
    key: string
    dayNumber: number | null
    dayLabel?: string
    dayDate?: string | null
    items: SelectedSession[]
  }[] = []
  const indexByKey: Record<string, number> = {}

  for (const session of sessions) {
    const key = [session.dayNumber ?? 'none', session.dayLabel ?? '', session.dayDate ?? ''].join('|')
    if (!(key in indexByKey)) {
      indexByKey[key] = groups.length
      groups.push({
        key,
        dayNumber: session.dayNumber,
        dayLabel: session.dayLabel,
        dayDate: session.dayDate,
        items: [],
      })
    }
    groups[indexByKey[key]].items.push(session)
  }

  return groups
}

export default function Confirmation(props: Props) {
  const {
    event,
    currentLocale,
    locales,
    approvalStatus = 'approved',
    eventUser,
    multiStep,
    qrCodeDataUrl,
    registrationTickets,
    agendaPdf,
    message,
    selectedSessions,
    urls,
    registrationLinkSlug,
    translations: t,
    ...shell
  } = props
  const isRtl = currentLocale === 'ar'
  const isPending = approvalStatus === 'pending'
  const isRejected = approvalStatus === 'rejected'
  const hideTickets = isPending || isRejected
  const registrationKey = hideTickets ? null : eventUser?.registrationKey
  const primaryTicket = hideTickets
    ? undefined
    : registrationTickets.find((ticket) => ticket.token === eventUser?.token) || registrationTickets[0]
  const companionTickets = hideTickets
    ? []
    : registrationTickets.filter((ticket) => ticket.token !== primaryTicket?.token)
  const entryQr = hideTickets ? null : (primaryTicket?.qrCodeDataUrl || qrCodeDataUrl)
  const showEntryTicket = !hideTickets && Boolean(entryQr || (multiStep && registrationKey) || primaryTicket)
  const sessionGroups = hideTickets ? [] : groupSelectedSessions(selectedSessions)
  const locale = currentLocale || 'en'
  const StatusIcon = isPending || isRejected ? Clock : CheckCircle
  const pageTitle = isPending
    ? (t.registrationPending || t.registrationSuccess)
    : isRejected
      ? (t.registrationRejected || t.registrationSuccess)
      : t.registrationSuccess

  return (
    <RegistrationPageLayout
      event={event}
      currentLocale={currentLocale}
      locales={locales}
      urls={urls}
      translations={t}
      pageTitle={`${pageTitle} – ${event.name}`}
      pageVariant="confirmation"
      registrationLinkSlug={registrationLinkSlug}
      {...shell}
    >
      <div className="ev-reg-card ev-reg-confirmation-card rounded-lg border p-6 sm:p-8">
        <div className="text-center">
          <StatusIcon
            className={`ev-reg-confirmation-icon mx-auto mb-4 h-16 w-16 ${isPending || isRejected ? 'text-[#D97706]' : ''}`}
          />
          <p className="ev-reg-confirmation-message mb-4 leading-relaxed">
            {message || (isPending ? t.registrationPending : isRejected ? t.registrationRejected : t.registrationSuccess)}
          </p>
          {eventUser && (
            <p className="ev-reg-confirmation-attendee mb-6 text-lg font-semibold">
              {[eventUser.firstName, eventUser.lastName].filter(Boolean).join(' ')}
            </p>
          )}
        </div>

        {showEntryTicket && (
          <div className="ev-reg-entry-ticket ev-reg-qr-wrap mb-6 overflow-hidden rounded-xl text-start">
            <div className="flex items-center gap-3 p-4">
              {entryQr && (
                <img src={entryQr} alt="QR Code" className="ev-reg-qr-image h-24 w-24 shrink-0 rounded-md bg-white" />
              )}
              <div className="min-w-0 flex-1">
                <p className="ev-reg-entry-ticket-title text-sm font-semibold">
                  {t.entranceTicket || t.ticketsIssued || 'Entrance Ticket'}
                </p>
                {multiStep && registrationKey && (
                  <div className="ev-reg-registration-key-wrap mt-1.5">
                    <p className="ev-reg-registration-key-label text-xs font-medium">
                      {t.yourRegistrationKey}
                    </p>
                    <p className="ev-reg-registration-key mt-1 font-semibold tracking-[0.12em]">
                      <span dir="ltr" className="inline-block select-all">
                        {registrationKey}
                      </span>
                    </p>
                    <p className="ev-reg-registration-key-hint mt-1.5 text-xs leading-relaxed">
                      {t.registrationKeyHint}
                    </p>
                  </div>
                )}
              </div>
            </div>
            {primaryTicket && (
              <div className="ev-reg-entry-ticket-actions grid grid-cols-2 border-t">
                <a
                  href={primaryTicket.downloadUrl}
                  className="ev-reg-ticket-link ev-reg-ticket-download inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium"
                >
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                  {t.downloadTicket}
                </a>
                <a
                  href={primaryTicket.badgeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ev-reg-ticket-link ev-reg-ticket-open inline-flex items-center justify-center gap-2 border-s px-3 py-3 text-sm font-medium"
                >
                  <FileText className="h-4 w-4 shrink-0" aria-hidden />
                  {t.openTicket}
                </a>
              </div>
            )}
          </div>
        )}

        {companionTickets.length > 0 && (
          <div className="ev-reg-tickets mb-6 space-y-3 text-start">
            {companionTickets.map((ticket) => (
              <div key={ticket.token} className="ev-reg-ticket overflow-hidden rounded-xl border">
                <div className={`flex items-start justify-between gap-3 p-4 ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                  <p className="ev-reg-ticket-name text-sm font-semibold">
                    {[ticket.firstName, ticket.lastName].filter(Boolean).join(' ')}
                  </p>
                  <img src={ticket.qrCodeDataUrl} alt="QR Code" className="ev-reg-ticket-qr h-16 w-16 shrink-0 rounded-md bg-white" />
                </div>
                <div className="ev-reg-entry-ticket-actions grid grid-cols-2 border-t">
                  <a href={ticket.downloadUrl} className="ev-reg-ticket-link ev-reg-ticket-download inline-flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium">
                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                    {t.downloadTicket}
                  </a>
                  <a href={ticket.badgeUrl} target="_blank" rel="noreferrer" className="ev-reg-ticket-link ev-reg-ticket-open inline-flex items-center justify-center gap-2 border-s px-3 py-3 text-sm font-medium">
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    {t.openTicket}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {!hideTickets && selectedSessions.length > 0 && (
          <div className="ev-reg-selected-sessions mb-6 text-start">
            <h2 className="ev-reg-selected-sessions-title mb-3 text-sm font-semibold tracking-wide">
              {t.yourSelectedSessions}
            </h2>

            <div className="ev-reg-selected-sessions-panel overflow-hidden rounded-xl border">
              <div
                className="ev-reg-session-arrival-notice flex items-start gap-2.5 px-4 py-3 text-sm leading-relaxed"
                dir={isRtl ? 'rtl' : 'ltr'}
              >
                <Clock className="ev-reg-session-arrival-notice-icon mt-0.5 h-4 w-4 shrink-0 opacity-80" aria-hidden />
                <p className="ev-reg-session-arrival-notice-text">{t.sessionArrivalNotice}</p>
              </div>

              <div className="ev-reg-selected-sessions-groups">
                {sessionGroups.map((group) => {
                  const dayDateLabel = formatAgendaDayDate(group.dayDate, locale)
                  const dayTitle =
                    group.dayNumber != null
                      ? resolveAgendaDayLabel(group.dayNumber, t.day, locale, group.dayLabel)
                      : group.dayLabel
                  const showDayHeading = Boolean(dayTitle || dayDateLabel)

                  return (
                    <div key={group.key} className="ev-reg-selected-sessions-day border-t border-dashed">
                      {showDayHeading && (
                        <div className="ev-reg-selected-sessions-day-heading px-4 pt-4">
                          {dayTitle && (
                            <div className="ev-reg-selected-sessions-day-title text-sm font-semibold tracking-wide">
                              {dayTitle}
                            </div>
                          )}
                          {dayDateLabel && (
                            <div className="ev-reg-selected-sessions-day-date mt-0.5 text-xs opacity-75">{dayDateLabel}</div>
                          )}
                        </div>
                      )}

                      <div className="ev-reg-selected-sessions-cards space-y-4 px-4 py-4">
                        {group.items.map((session) => {
                          const hasTime = !!(session.time || session.endTime)

                          return (
                            <div
                              key={session.id}
                              className="ev-reg-selected-session-card"
                              dir={isRtl ? 'rtl' : 'ltr'}
                            >
                              {hasTime && (
                                <div className="ev-reg-selected-session-card-header mb-2.5">
                                  <span className="ev-reg-session-time-badge inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs tabular-nums">
                                    <Clock className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
                                    <SessionTimeRange
                                      start={session.time}
                                      end={session.endTime}
                                      locale={locale}
                                      className="ev-reg-selected-session-time"
                                    />
                                  </span>
                                </div>
                              )}
                              <div
                                className="ev-reg-selected-session-description ev-reg-session-description-html text-sm font-medium leading-relaxed [&_p]:m-0 [&_p+p]:mt-1 [&_ul]:my-1 [&_ul]:ps-5 [&_ul]:list-disc [&_strong]:font-semibold"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeRichTextHtml(session.description),
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {!hideTickets && agendaPdf && (
          <div className="ev-reg-agenda-pdf mb-6 grid grid-cols-2 gap-3">
            <a
              href={agendaPdf.url}
              download={agendaPdf.filename}
              className="ev-reg-agenda-pdf-download inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium"
            >
              <Download className="h-4 w-4 shrink-0" aria-hidden />
              {t.downloadAgenda || 'Download agenda'}
            </a>
            <a
              href={agendaPdf.url}
              target="_blank"
              rel="noreferrer"
              className="ev-reg-agenda-pdf-view inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium"
            >
              <FileText className="h-4 w-4 shrink-0" aria-hidden />
              {t.viewAgenda || 'View agenda'}
            </a>
          </div>
        )}

        <div className="ev-reg-confirmation-actions pt-1">
          <Button className="ev-reg-button ev-reg-back-button h-11 w-full text-sm font-semibold" onClick={() => router.visit(urls.register)}>
            <ArrowLeft className={`h-4 w-4 ${isRtl ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {t.backToRegistration}
          </Button>
        </div>
      </div>
    </RegistrationPageLayout>
  )
}
